import { Loader2 } from 'lucide-react';
import type {
  BusinessRelationshipDetail,
  UntradedDrug,
} from '@/entities/business-relationship/api/businessRelationship.api';
import { useUntradedDrugs } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { AppOnlyButton } from '@/shared/components/app-guide/AppOnlyButton';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import {
  Sheet,
  SheetContent,
  SheetDescription,
} from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useLoadMoreSentinel } from '@/shared/hooks/useLoadMoreSentinel';
import { cn } from '@/shared/lib/utils';
import { formatNumber, formatShortYm } from '@/shared/utils/format';

import { DrawerHeader } from './drawerParts';

const SectionHeader = ({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'success' | 'muted';
}) => (
  <div className="flex items-center border-b border-line-soft bg-subtle px-6 py-3.5">
    <span
      className={cn(
        'text-[13px] leading-5 font-bold',
        tone === 'success' ? 'text-success' : 'text-ink-4',
      )}
    >
      {label}
    </span>
    <span className="ml-auto text-xs leading-[18px] text-ink-6">{count}개</span>
  </div>
);

const DrugRow = ({ drug }: { drug: UntradedDrug }) => (
  <li className="border-b border-line-soft px-6 py-[18px]">
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-badge bg-primary-soft px-2 py-[3px] text-[11px] leading-[17px] font-medium text-primary',
          !drug.canApply && 'opacity-60',
        )}
      >
        처방 수량 {formatNumber(drug.totalOutHospitalQuantity)}
      </span>
      <span
        className={cn(
          'min-w-0 truncate text-xs leading-[18px]',
          drug.canApply ? 'text-ink-5' : 'text-ink-7',
        )}
      >
        {drug.pharmaCompanyName ?? '제약사 정보 없음'}
      </span>
    </div>
    <p
      className={cn(
        'mt-2 text-[15px] leading-[22px] font-medium break-keep',
        drug.canApply ? 'text-ink' : 'text-ink-4',
      )}
    >
      {drug.drugName}
    </p>
    {drug.canApply ? (
      <div className="mt-2.5 flex justify-end">
        <AppOnlyButton feature="filteringApply" className="w-[116px]">
          필터링 신청
        </AppOnlyButton>
      </div>
    ) : (
      drug.blockReasonLabel && (
        <p className="mt-2 flex items-center gap-1.5 text-xs leading-[18px] text-danger">
          <span className="size-1.5 rounded-full bg-danger" aria-hidden />
          {drug.blockReasonLabel}
        </p>
      )
    )}
  </li>
);

/** 한 구역(가능 · 불가) — 각자 페이지를 이어 받는다. 끝이 보이면 다음 페이지 */
const DrugSection = ({
  label,
  tone,
  count,
  query,
}: {
  label: string;
  tone: 'success' | 'muted';
  count: number;
  query: ReturnType<typeof useUntradedDrugs>;
}) => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const sentinelRef = useLoadMoreSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  if (count === 0) return null;
  const items = data?.pages.flatMap((p) => p.content) ?? [];

  return (
    <section>
      <SectionHeader label={label} count={count} tone={tone} />
      {query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : (
        <ul>
          {items.map((d, i) => (
            <DrugRow key={`${d.drugCode}-${d.pharmaCompanyId}-${i}`} drug={d} />
          ))}
        </ul>
      )}
      {(query.isPending || hasNextPage) && (
        <div ref={sentinelRef} className="flex justify-center p-5" aria-hidden>
          {(query.isPending || isFetchingNextPage) && (
            <Loader2 className="size-5 animate-spin text-ink-7" />
          )}
        </div>
      )}
    </section>
  );
};

/**
 * 목록 본문 — 신청 가능(canApply=true) · 불가(false)를 따로 불러 각자 페이징한다.
 * 불가 구역은 가능 구역을 끝까지 받은 뒤에 부른다 (순서대로 이어 보이게).
 * 구역 건수는 필터 · 페이지와 무관한 서버 전체 건수를 쓴다.
 */
const MissedDrugsList = ({ relationshipId }: { relationshipId: number }) => {
  const possible = useUntradedDrugs(relationshipId, true);
  const impossible = useUntradedDrugs(
    relationshipId,
    false,
    possible.isSuccess && !possible.hasNextPage,
  );

  if (possible.isPending) {
    return (
      <div className="flex flex-col gap-3 p-6">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }
  if (possible.isError || !possible.data)
    return <ErrorState onRetry={() => possible.refetch()} />;

  const first = possible.data.pages[0];
  if (
    !first.standardYearMonth ||
    first.applicableCount + first.nonApplicableCount === 0
  ) {
    return (
      <EmptyState
        className="py-16"
        message={
          first.standardYearMonth
            ? '최근 처방 내역에 미거래 의약품이 없어요.'
            : '확정된 처방 내역이 없어요.'
        }
      />
    );
  }

  return (
    <>
      <DrugSection
        label="필터링 가능"
        tone="success"
        count={first.applicableCount}
        query={possible}
      />
      <DrugSection
        label="필터링 불가"
        tone="muted"
        count={first.nonApplicableCount}
        query={impossible}
      />
    </>
  );
};

/**
 * M-22 놓치고 있는 의약품 드로어 (640) — 목록은 웹, 신청은 앱(X6).
 * 기준은 최신 확정 처방월(상세 standardYearMonth). 확정 처방이 없으면 null로 온다.
 */
export const MissedDrugsDrawer = ({
  detail,
  open,
  onClose,
}: {
  detail: BusinessRelationshipDetail;
  open: boolean;
  onClose: () => void;
}) => {
  const ym = detail.standardYearMonth;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" overlay="drawer">
        <DrawerHeader title="놓치고 있는 의약품" onClose={onClose}>
          <p className="flex flex-wrap items-center gap-x-2 text-[13px] leading-5">
            <span className="font-medium text-ink">{detail.clinicName}</span>
            {ym && (
              <>
                <span className="text-ink-faint">·</span>
                <span className="text-ink-6">
                  {formatShortYm(ym)} 확정 처방 기준
                </span>
              </>
            )}
          </p>
        </DrawerHeader>
        <SheetDescription className="mt-2 shrink-0 border-b border-line px-6 pb-[18px] text-xs leading-[18px] text-ink-6">
          확정된 처방 중 아직 거래하지 않는 의약품입니다. 필터링 신청을 하면 그
          처방이 정산 대상이 됩니다.
        </SheetDescription>

        <div className="flex-1 overflow-y-auto">
          {open &&
            (ym ? (
              <MissedDrugsList relationshipId={detail.relationshipId} />
            ) : (
              <EmptyState
                className="py-16"
                message="확정된 처방 내역이 없어요."
              />
            ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
